<template>
  <div class="submit-resume-container">
    <ReferralResume
      v-if="ready"
      ref="editor"
      class="resume-editor"
      :value="candidateInfo"
      :stepSetting="resumeEditorSetting"
      :isCandidate="true"
      :isNewPage="true"
      saveBtnTextKey="common.btn.submit"
      @cancel="handleCancel"
      @save="handleSave"
      @save-back="handleSaveAndBack"
    />
    <DialogWrapper
      class="submit-success-dialog"
      :show.sync="submitSuccessDialog.show"
      :close-on-click-modal="false"
      :showClose="false"
      :hideFooter="true"
    >
      <div class="submit-success-container">
        <i class="result-icon iconfont iconcompleted"/>
        <span class="title-label">{{ $t(`${i18nPrefix}.label.referralSuccess`) }}</span>
        <span class="title-tips">{{ $t(`${i18nPrefix}.tips.referralSuccessTips`) }}</span>
        <el-button class="button view-dashboard" type="primary" @click="handelToDashboard">{{ $t(`${i18nPrefix}.btn.seeDashboard`) }}</el-button>
        <el-button class="button submit-more" @click="handelSubmitNew">{{ $t(`${i18nPrefix}.btn.moreReferral`) }}</el-button>
      </div>
    </DialogWrapper>
    <AIResumeParseLoadingDialog :isShow="showAIResumeParseDialog"/>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import ReferralResume from '@/components/candidate/editor/layout.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { mixedRouterPush } from '@/router/routes'
import { STEP_TYPE } from '@/constant/candidate'
import { generateTempFileKey, getFileExtension, uploadFile } from '@/utils/oss'
import AIResumeParseLoadingDialog from '@/components/dialog/aiResumeParseLoadingDialog.vue'

const REFERRAL_NEED_UPLOAD_RESUME_COMPANY_IDS = [64, 8190, 6426, 8491, 6143]

export default {
  components: {
    AIResumeParseLoadingDialog,
    ReferralResume,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix: 'personal.resume',
      ready: false,
      readyPromise: [],
      hiddenIdNumber: true,
      candidateInfo: {},
      hiddenField: [],
      accountInfo: {},
      miniResumeHiddenStep: [STEP_TYPE.EDUCATION, STEP_TYPE.LANGUAGE, STEP_TYPE.ADDITIONAL_INFO],
      resumeEditorSetting: {
        hiddenStep: [
          STEP_TYPE.UPLOAD_RESUME,
          STEP_TYPE.BASE_INFO,
          STEP_TYPE.EDUCATION,
          STEP_TYPE.LANGUAGE,
          STEP_TYPE.DESCRIPTION,
          STEP_TYPE.WORK_EXPERIENCE,
          STEP_TYPE.ADDITIONAL_INFO,
          STEP_TYPE.PRESCREEN_QUESTIONNAIRE,
          STEP_TYPE.JOB_QUESTIONNAIRE,
          STEP_TYPE.INVITE_QUESTIONNAIRE,
          STEP_TYPE.HR_QUESTIONNAIRE,
          STEP_TYPE.ONBOARDING_QUESTIONNAIRE],
        displayStep: [STEP_TYPE.SUBMIT_REFERRAL],
        [STEP_TYPE.SUBMIT_REFERRAL]: {
          needUploadResume: false,
          noNeedReason: false
        }
      },
      submitSuccessDialog: {
        show: false
      },
      showAIResumeParseDialog: false
    }
  },
  computed: {
    ...mapGetters('company', ['companyThemeSetting']),
    jobId () {
      return this.$route.query?.job_id
    },
    companyId () {
      const companyId = this.$route.query?.company_id
      return companyId && _.toNumber(companyId)
    },
    useAIResumeParse () {
      return this.companyThemeSetting?.company.has_ai_resume_parse_premium
    }
  },
  async created () {
    if (this.companyId) {
      this.resumeEditorSetting[STEP_TYPE.SUBMIT_REFERRAL].needUploadResume = REFERRAL_NEED_UPLOAD_RESUME_COMPANY_IDS.includes(this.companyId)
      this.resumeEditorSetting[STEP_TYPE.SUBMIT_REFERRAL].noNeedReason = this.companyId === 6143
    }
    this.accountInfo = this.getCurrentAccount()
    if (!this.isCandidateOrEmployee()) {
      this.$router.replace('/error/no-permission')
      return
    }

    this.ready = true

    if (!this.jobId) {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(backUrl)
      } else if (this.jobId) {
        this.$router.push(`/job-detail-new/${this.jobId}/c/`)
      } else {
        this.$router.push({ path: '/recruiting/personal/home', query: { company_id: this.companyId } })
      }
    } else {
      await this.initJobRequiredInfo()
    }
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('personal', ['submitResumeToReferral']),
    ...mapActions('application', ['getJobApplyRequiredInfo']),
    isCandidateOrEmployee () {
      if (!this.accountInfo) {
        return true
      }
      if (this.accountInfo.type === 'candidate') {
        return true
      }
      if (this.hasAnyPermission('COMPUTED_ACCOUNT_IS_EMPLOYEE')) {
        return true
      }
      return false
    },
    async handleCancel () {
      let backUrl = this.$route.query.back
      backUrl = _.replace(backUrl, 'go_to_submit=1', 'go_to_submit=0')
      if (backUrl) {
        mixedRouterPush(backUrl)
      } else if (this.jobId) {
        this.$router.push(`/job-detail-new/${this.jobId}/c/`)
      } else {
        this.$router.push({ path: '/recruiting/personal/home', query: { company_id: this.companyId } })
      }
    },
    async initJobRequiredInfo () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.getJobApplyRequiredInfo(this.jobId)
        if (resp) {
          if ((resp.required_field || []).includes('resume')) {
            this.resumeEditorSetting[STEP_TYPE.SUBMIT_REFERRAL].needUploadResume = true
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleSave (candidateInfo, autoBack) {
      const referralInfo = candidateInfo.referralData
      if (this.useAIResumeParse) {
        this.showAIResumeParseDialog = true
      } else {
        this.$eventBus.$emit('showLoading', true)
      }
      try {
        const resumeFile = referralInfo.resumeFile
        let fileKey
        if (resumeFile) {
          const fileExt = getFileExtension(resumeFile)
          fileKey = generateTempFileKey(fileExt)
          await uploadFile(resumeFile, fileKey)
        }
        const params = {
          phone: referralInfo.phoneNumber,
          submit_file_name: resumeFile?.name,
          email_address: referralInfo.emailAddress,
          current_company: referralInfo.currentCompany,
          current_position: referralInfo.currentPosition,
          referral_reason: referralInfo.referralReason,
          first_name: referralInfo.firstName,
          last_name: referralInfo.lastName,
          resume_file: fileKey,
          referral_account: this.accountInfo.id,
          job_id: this.jobId
        }
        const resp = await this.submitResumeToReferral(params)
        if (resp && resp.status === 'success') {
          this.submitSuccessDialog.show = true
        } else if (_.includes([20006042], resp.code)) {
          this.$message.error(this.$t(`error.code.${resp.code}`))
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.tips.submitFailed`))
        }
      } catch (error) {
        this.$message.error(this.$t(`${this.i18nPrefix}.tips.submitFailed`))
      } finally {
        if (this.useAIResumeParse) {
          this.showAIResumeParseDialog = false
        } else {
          this.$eventBus.$emit('showLoading', false)
        }
      }
    },
    handleSaveAndBack (referralInfo) {
      return this.handleSave(referralInfo, true)
    },
    fileToBase64 (file) {
      if (!file) {
        return ''
      }
      const reader = new FileReader()
      // 将文件加载进入
      reader.readAsDataURL(file)
      return new Promise((resolve, reject) => {
        reader.onload = function () {
          resolve(this.result)
        }
      })
    },
    handelToDashboard () {
      window.location.href = `/recruiting/company/${this.companyId}/job-list/?scroll_to_list=1`
    },
    handelSubmitNew () {
      this.ready = false
      this.$nextTick(() => {
        this.ready = true
      })
      this.submitSuccessDialog.show = false
    }
  }
}
</script>

<style lang="scss" scoped>
.submit-resume-container {

  .submit-success-dialog {
    .submit-success-container {
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: center;

      .result-icon {
        font-size: 50px;
        color: #52C41A;
      }

      .title-label {
        margin-top: 32px;
        font-size: 22px;
        font-weight: 500;
        color: #303133;
        line-height: 30px;
        word-break: break-word;
        white-space: normal;
        text-align: center;
      }
      .title-tips {
        margin-top: 50px;
        height: 22px;
        font-size: 16px;
        font-weight: 400;
        color: #606266;
        line-height: 22px;
      }

      .button {
        width: 260px;
        height: 40px;
        margin: 0;
        border-radius: 2px;
      }

      .view-dashboard {
        margin-top: 50px;
      }

      .submit-more {
        margin-top: 12px;
        background: #EDEDED;
        margin-bottom: 50px;
        color: #0096cc;
      }
    }
  }

  ::v-deep .new-page-layout {
    .layout-main {

      background-color: unset;
      padding-top: 15px;

      .layout-content {
        width: 700px;
      }

      .adapt-wrapper {
        justify-content: center;

        .el-button  {
          width: 90px;

          &:first-child {
            margin-right: 420px;
          }
        }
      }
    }
  }
}
</style>
